import { defineComponent as _defineComponent } from 'vue'
import {useI18n} from "@/composables/useI18n";

import {
  FloatingActionButton
} from '@/components/buttons';

import {
  computed,
  ref
} from 'vue'

import type {
  Issuer as IssuerType,
} from "@/model/Issuer";

import {
  IssuerSearchFilter
} from '@/components/issuers/children';

import {
  IssuerStatus,
} from "@/Enums";

import {
  deleteIssuer,
  fetchIssuers,
} from "@/components/issuers/scripts/issuers/requests";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  useDataTableDefaults,
} from "@/composables/useDataTableDefaults";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editIssuersPolicy,
} from "@/authorization/policies";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import {
  ISSUER_ID_STORAGE_KEY,
} from "@/components/issuers/scripts";

import ConfirmationDialog from "@/components/base/ConfirmationDialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'IssuerManagement',
  emits: [
  'onSelectIssuerDetails',
  'onSelectIssuerCards',
  'onSelectConfiguredWebcharges',
  'onCreateNewIssuer',
  'onSelectReports',
  'onSelectUserManagement',
  'onFetchError'
],
  setup(__props, { emit: emits }) {

const {
  t,
} = useI18n();

const {
  pagination,
  loading,
  dataTableFooter,
} = useDataTableDefaults();

const selectedIssuerId = ref<number>();
const dataTableOptions = ref();
const issuers = ref<Array<IssuerType>>();

const deleteIssuerDialog = ref();

let fetchIssuerFilterArgs : { issuerState?: IssuerStatus, companyName?: string, phoneNumber?: string, email?: string, contactName?: string } = {};



const headers = computed(() => {
  return [
    {
      align: "start",
      text: t("Issuer.CompanyName"),
      value: "CompanyName",
      key: "CompanyName"
    },
    {
      align: "start",
      text: t("Issuer.ContactEmployee"),
      value: "ContactEmployee.FullName",
      key: "ContactEmployee.FullName"
    },
    {
      align: "start",
      text: t("Issuer.Phone"),
      filterable: false,
      sortable: false,
      value: "ContactEmployee.phoneNumber"
    },
    {
      align: "start",
      text: t("Issuer.Email"),
      value: "ContactEmployee.email",
      key: "ContactEmployee.email"
    },
    {
      align: "start",
      text: t("Issuer.State"),
      filterable: false,
      sortable: false,
      value: "state"
    },
    {
      align: "start",
      text: t("Issuer.Action"),
      filterable: false,
      sortable: false,
      value: "edit"
    }
  ]
});

const hasNoEditRights = computed(() => {
  return !hasAccessRights(editIssuersPolicy)
});

const translateState = (status: IssuerStatus) => {
  switch (status) {
    case IssuerStatus.Interested:
      return t("State.Interested");
    case IssuerStatus.Contracted:
      return t("State.Contracted");
    case IssuerStatus.Confirmed:
      return t("State.Confirmed");
    case IssuerStatus.Canceled:
      return t("State.Canceled");
    default:
      return t("State.Unknown");
  }
};

const headerSortValueToSortColumn = (value: string) => {
  switch (value) {
    case "CompanyName":
      return "companyname";
    case "ContactEmployee.FullName":
      return "contactname";
    case "ContactEmployee.Email":
      return "email";
    default:
      return "companyname";
  }
};

const onHandleFetchError = ({error}: {
  error: any
}) => {
  emits('onFetchError', {error})
};

const onSelectIssuerDetails = (id: number) => {
  saveItemToStorage(id, ISSUER_ID_STORAGE_KEY);
  emits('onSelectIssuerDetails');
};

const onSelectIssuerCards = (id: number) => {
  saveItemToStorage(id, ISSUER_ID_STORAGE_KEY);
  emits('onSelectIssuerCards');
};

const onSelectConfiguredWebcharges = (id: number) => {
  saveItemToStorage(id, ISSUER_ID_STORAGE_KEY);
  emits('onSelectConfiguredWebcharges');
};

const onSelectReports = (id: number) => {
  saveItemToStorage(id, ISSUER_ID_STORAGE_KEY);
  emits('onSelectReports');
};

const onSelectUserManagement = (id: number) => {
  saveItemToStorage(id, ISSUER_ID_STORAGE_KEY);
  emits('onSelectUserManagement');
};

const onCreateNewIssuer = () => {
  emits('onCreateNewIssuer');
};

const onSelectDeleteIssuer = async (issuerId: number) => {
  selectedIssuerId.value = issuerId;
  deleteIssuerDialog.value?.Show();
}

const onDeleteIssuer = async () => {
  if(!selectedIssuerId.value) return;

  const {error} = await deleteIssuer(selectedIssuerId.value);
  selectedIssuerId.value = undefined;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  loadIssuers();
}


const loadIssuers = async () => {
  const _sortColumn = dataTableOptions.value.sortBy;
  const _sortDirection = dataTableOptions.value.sortDesc;

  const fetchArgs: FetchIssuersParam = {
    pageNumber: dataTableOptions.value.page,
    pageSize: dataTableOptions.value.itemsPerPage
  };

  // add sorting, if set
  if(_sortColumn.length === 1 && _sortDirection.length === 1){ // if use multisort, this could have length > 1, but we only use single sort
    fetchArgs.sortColumn = headerSortValueToSortColumn(_sortColumn[0]);
    fetchArgs.sortDirection = _sortDirection[0] ? "desc" : "asc";
  }

  // set search filter
  fetchArgs.companyName = fetchIssuerFilterArgs.companyName;
  fetchArgs.phoneNumber = fetchIssuerFilterArgs.phoneNumber;
  fetchArgs.email = fetchIssuerFilterArgs.email;
  fetchArgs.contactName = fetchIssuerFilterArgs.contactName;
  fetchArgs.issuerState = fetchIssuerFilterArgs.issuerState;

  loading.value = true;

  const {
    response,
    error,
    totalItemCount,
  } = await fetchIssuers(fetchArgs);

  loading.value = false;

  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
  pagination.value.totalItemCount = totalItemCount ?? 0;
}

const onUpdateDataTabelOptions = () => {
  loadIssuers();
}

const onSearchFilterChange = (searchFilter: { issuerState?: IssuerStatus, companyName?: string, phoneNumber?: string, email?: string, contactName?: string }) =>{
  fetchIssuerFilterArgs = searchFilter;

  // if we are on page 1, we can just reload the data
  if (dataTableOptions.value.page === 1) {
    loadIssuers();
    return;
  }

  // if we are not on page 1, switch to page 1, which will trigger loadIssuers() in onUpdateDataTabelOptions
  dataTableOptions.value.page = 1;
}

return { __sfc: true,t, pagination, loading, dataTableFooter, selectedIssuerId, dataTableOptions, issuers, deleteIssuerDialog, fetchIssuerFilterArgs, emits, headers, hasNoEditRights, translateState, headerSortValueToSortColumn, onHandleFetchError, onSelectIssuerDetails, onSelectIssuerCards, onSelectConfiguredWebcharges, onSelectReports, onSelectUserManagement, onCreateNewIssuer, onSelectDeleteIssuer, onDeleteIssuer, loadIssuers, onUpdateDataTabelOptions, onSearchFilterChange, FloatingActionButton, IssuerSearchFilter, IssuerStatus, ConfirmationDialog }
}

})